import { useMutation, useQuery, useQueryClient } from "react-query";
import * as notificationApi from './notificationApi'
import useAuthToken, { getAuthToken } from "../../../../hooks/useAuthToken";
import { useUnifyAdminStore } from '../../AdminStore';

export function useGetAllNotifications(isArchive: boolean) {
    return useQuery(
        !isArchive ? 'getAllFeatureReleaseNotifications' : 'getAllArchiveFeatureReleaseNotifications',
        () => !isArchive ?
            notificationApi.fetchListOfFeatureReleaseNotification(getAuthToken())
            : notificationApi.fetchListOfArchiveFeatureReleaseNotification(getAuthToken()),
    )
}

type TranslatorResult = Record<string, string>;

type UseTranslatorOptions = {
    keys?: string[];
    targetLang?: string;
    onSuccess?: (translated: TranslatorResult, raw: any) => void;
    onError?: (err: unknown) => void;
};

export const useCreateQuery = ({ onSuccess, onError }: any) => {
    const token = useAuthToken();
    const resetReleaseNotificationSideSheetState = useUnifyAdminStore(
        (state) => state.resetReleaseSideSheetState
    );
    const queryClient = useQueryClient();

    const mutation = useMutation((model: any) =>
        notificationApi.createNotification(token, model), {
        onError: (error: any) => {
            onError();
            console.error(error);
        },
        onSuccess: (data: any) => {
            onSuccess();
            queryClient.invalidateQueries(['getAllFeatureReleaseNotifications']);
            resetReleaseNotificationSideSheetState();
        },
    });

    return { mutation };
};


export const useUpdateQuery = ({ onSuccess, onError }: any) => {
    const token = useAuthToken();
    const resetReleaseNotificationSideSheetState = useUnifyAdminStore(
        (state) => state.resetReleaseSideSheetState
    );
    const queryClient = useQueryClient();

    const mutation = useMutation((model: any) =>
        notificationApi.updateNotification(token, model), {
        onError: (error: any) => {
            onError();
            console.error(error);
        },
        onSuccess: (data: any) => {
            onSuccess();
            queryClient.invalidateQueries(['getAllFeatureReleaseNotifications']);
            resetReleaseNotificationSideSheetState();
        },
    });

    return { mutation };
};


export const useRemoveQuery = ({ onSuccess, onError }: any) => {
    const token = useAuthToken();
    const resetReleaseNotificationSideSheetState = useUnifyAdminStore(
        (state) => state.resetReleaseSideSheetState
    );
    const queryClient = useQueryClient();

    const mutation = useMutation((model: any) =>
        notificationApi.removeNotification(token, model), {
        onError: (error: any) => {
            onError();
            console.error(error);
        },
        onSuccess: (data: any) => {
            onSuccess();
            queryClient.invalidateQueries(['getAllFeatureReleaseNotifications']);
            resetReleaseNotificationSideSheetState();
        },
    });

    return { mutation };
};

export const useTranslator = ({
    keys = ["title", "body", "subject"],
    targetLang,
    onSuccess,
    onError,
}: UseTranslatorOptions = {}) => {
    const token = useAuthToken();
    let lastRaw: any;

    const mutation = useMutation<TranslatorResult, unknown, any>({
        mutationFn: async (model: any) => {
            const resp = await notificationApi.runTranslator(token, model);
            const data = Array.isArray(resp) ? resp : resp?.data;
            if (!Array.isArray(data)) {
                throw new Error("Translator: unexpected response shape (not an array).");
            }
            lastRaw = data;

            const pickText = (item: any): string => {
                const arr = item?.translations;
                if (!Array.isArray(arr) || arr.length === 0) return "";
                if (targetLang) {
                    const found = arr.find((t: any) => t?.to === targetLang);
                    if (found && typeof found.text === "string") return found.text;
                }
                return typeof arr[0]?.text === "string" ? arr[0].text : "";
            };

            const translated = keys.reduce((acc, key, i) => {
                acc[key] = pickText(data[i]);
                return acc;
            }, {} as Record<string, string>);

            return translated;
        },
        onSuccess: (translated) => {
            onSuccess?.(translated, lastRaw);
        },
        onError,
    });

    return { mutation };
};